import React, { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "../components/layout/SiteLayout";
import { api, formatApiErrorDetail, resolveAssetUrl } from "../lib/api";
import { useSettings } from "../lib/settings";
import { Loader2, CheckCircle2, Coffee, MapPin, Clock, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const initial = {
  name: "", email: "", phone: "",
  party_adults: 1, party_kids: 0, kids_ages: "",
  planned_date: "", service_time: "",
  heard_from: "", notes: "",
};

export default function PlanVisitPage() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ ...initial, service_time: settings.sunday_service_2 });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  React.useEffect(() => {
    if (!form.service_time && settings.sunday_service_2) {
      setForm((f) => ({ ...f, service_time: settings.sunday_service_2 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.sunday_service_2]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email so we can reach you.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        party_adults: Number(form.party_adults) || 1,
        party_kids: Number(form.party_kids) || 0,
      };
      await api.post("/visits", payload);
      setSent(true);
      toast.success("We've got it. A pastor will be in touch within 48 hours.");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Could not submit.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <SiteLayout>
        <section className="container-x py-28 lg:py-40 min-h-[70vh]">
          <div data-testid="visit-success" className="max-w-3xl mx-auto bg-[hsl(var(--sand))] border border-[hsl(var(--line))] p-10 lg:p-16 text-center">
            <CheckCircle2 className="mx-auto text-[hsl(var(--moss))]" size={44} />
            <h1 className="display text-4xl lg:text-5xl mt-6">We're saving you a seat.</h1>
            <p className="mt-5 text-[hsl(var(--ink-muted))] leading-relaxed max-w-xl mx-auto">
              Thanks {form.name.split(" ")[0]} — a pastor will personally reach out within 48 hours
              with parking, kids check-in details, and a friendly face to look for on Sunday.
            </p>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 text-left text-sm max-w-2xl mx-auto">
              <div className="border-l-2 border-[hsl(var(--terracotta))] pl-4 py-1">
                <div className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--ink-muted))]">Where</div>
                <div className="mt-1">{settings.address_line1}<br/>{settings.address_line2}</div>
              </div>
              <div className="border-l-2 border-[hsl(var(--terracotta))] pl-4 py-1">
                <div className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--ink-muted))]">When</div>
                <div className="mt-1">Sundays · {form.service_time}</div>
              </div>
              <div className="border-l-2 border-[hsl(var(--terracotta))] pl-4 py-1">
                <div className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--ink-muted))]">Wear</div>
                <div className="mt-1">Whatever feels like you. Truly.</div>
              </div>
            </div>
            <Link
              to="/"
              data-testid="visit-success-home"
              className="mt-12 inline-flex items-center gap-2 px-7 py-3.5 rounded-sm bg-[hsl(var(--terracotta))] text-[hsl(var(--linen))] text-sm tracking-wide lift"
            >
              Back to home <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <section className="container-x py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">{settings.visit_eyebrow}</div>
            <h1 className="display text-5xl lg:text-7xl">{settings.visit_headline}</h1>
            <p className="mt-6 text-[hsl(var(--ink-muted))] leading-relaxed">
              {settings.visit_intro}
            </p>

            <div className="mt-12 space-y-6 text-sm">
              <div className="flex items-start gap-3">
                <Coffee size={18} className="mt-0.5 text-[hsl(var(--terracotta))]" />
                <div>
                  <div className="text-[hsl(var(--ink))]">{settings.visit_perk_1_title}</div>
                  <div className="text-[hsl(var(--ink-muted))]">{settings.visit_perk_1_text}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Sparkles size={18} className="mt-0.5 text-[hsl(var(--terracotta))]" />
                <div>
                  <div className="text-[hsl(var(--ink))]">{settings.visit_perk_2_title}</div>
                  <div className="text-[hsl(var(--ink-muted))]">{settings.visit_perk_2_text}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock size={18} className="mt-0.5 text-[hsl(var(--terracotta))]" />
                <div>
                  <div className="text-[hsl(var(--ink))]">{settings.visit_perk_3_title}</div>
                  <div className="text-[hsl(var(--ink-muted))]">{settings.visit_perk_3_text}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={18} className="mt-0.5 text-[hsl(var(--terracotta))]" />
                <div>
                  <div className="text-[hsl(var(--ink))]">{settings.address_line1}</div>
                  <div className="text-[hsl(var(--ink-muted))]">Free parking on-site.</div>
                </div>
              </div>
            </div>

            <div className="mt-12 hidden lg:block">
              <div className="aspect-[5/4] border border-[hsl(var(--line))] overflow-hidden">
                {settings.visit_image_url && (
                  <img src={resolveAssetUrl(settings.visit_image_url)} alt="" className="w-full h-full object-cover" />
                )}
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="visit-form"
            className="lg:col-span-7 bg-[hsl(var(--sand))] border border-[hsl(var(--line))] p-8 lg:p-12 rounded-sm space-y-6"
          >
            <div className="display text-3xl">Tell us about your visit</div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Your name" required>
                <input data-testid="visit-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={fieldCls} />
              </Field>
              <Field label="Email" required>
                <input data-testid="visit-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={fieldCls} />
              </Field>
              <Field label="Phone (optional)">
                <input data-testid="visit-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={fieldCls} />
              </Field>
              <Field label="How did you hear about us?">
                <select data-testid="visit-heard-from" value={form.heard_from} onChange={(e) => set("heard_from", e.target.value)} className={fieldCls}>
                  <option value="">Select…</option>
                  <option>A friend invited me</option>
                  <option>Google / search</option>
                  <option>Instagram</option>
                  <option>Walked past the building</option>
                  <option>A previous visit</option>
                  <option>Other</option>
                </select>
              </Field>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              <Field label="# of adults">
                <input data-testid="visit-adults" type="number" min="1" value={form.party_adults} onChange={(e) => set("party_adults", e.target.value)} className={fieldCls} />
              </Field>
              <Field label="# of kids">
                <input data-testid="visit-kids" type="number" min="0" value={form.party_kids} onChange={(e) => set("party_kids", e.target.value)} className={fieldCls} />
              </Field>
              <Field label="Kids' ages (optional)">
                <input data-testid="visit-kids-ages" value={form.kids_ages} onChange={(e) => set("kids_ages", e.target.value)} placeholder="e.g. 3, 6, 9" className={fieldCls} />
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Sunday you're planning to visit">
                <input data-testid="visit-date" type="date" value={form.planned_date} onChange={(e) => set("planned_date", e.target.value)} className={fieldCls} />
              </Field>
              <Field label="Which service?">
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {[settings.sunday_service_1, settings.sunday_service_2].filter(Boolean).map((t) => (
                    <button
                      type="button"
                      key={t}
                      data-testid={`visit-time-${t.replace(/[^0-9]/g, "")}`}
                      onClick={() => set("service_time", t)}
                      className={`py-3 rounded-sm border text-sm lift ${
                        form.service_time === t
                          ? "border-[hsl(var(--terracotta))] bg-[hsl(var(--linen))]"
                          : "border-[hsl(var(--line))] bg-[hsl(var(--linen))]/60"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </Field>
            </div>

            <Field label="Anything we should know? (mobility needs, questions, prayer requests)">
              <textarea data-testid="visit-notes" rows={4} value={form.notes} onChange={(e) => set("notes", e.target.value)} className={fieldCls} />
            </Field>

            <button
              type="submit"
              data-testid="visit-submit"
              disabled={busy}
              className="lift inline-flex w-full sm:w-auto items-center justify-center gap-2 px-8 py-4 rounded-sm bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta-deep))] text-[hsl(var(--linen))] text-sm tracking-wide disabled:opacity-60"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              {settings.visit_submit_label || "Save my seat"}
            </button>
            <p className="text-xs text-[hsl(var(--ink-muted))]">
              {settings.visit_form_disclaimer}
            </p>
          </form>
        </div>
      </section>
    </SiteLayout>
  );
}

const fieldCls =
  "mt-2 w-full bg-[hsl(var(--linen))] border border-[hsl(var(--line))] px-4 py-3 rounded-sm focus:outline-none focus:border-[hsl(var(--terracotta))]";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-muted))]">
        {label} {required && <span className="text-[hsl(var(--terracotta))]">*</span>}
      </span>
      {children}
    </label>
  );
}
